import { ShieldCheck, Shuffle, TrendingUp } from "lucide-react";

import { kanziSections } from "../../../../content/kanziSections";
import Reveal from "../../Reveal";
import SectionHeading from "../../SectionHeading";
import SpotlightCard from "../../SpotlightCard";

const icons = { ShieldCheck, TrendingUp, Shuffle };

const HowItWorks = () => (
  <section id="how-it-works" className="section-block">
    <div className="section-shell">
      <div className="kanzi-how-split">
        <Reveal variant="fade-right" className="kanzi-how-split__left">
          <SectionHeading
            eyebrow={kanziSections.howItWorks.eyebrow}
            title={kanziSections.howItWorks.title}
          />
          <div className="kanzi-how-intro">
            <p className="kanzi-how-intro__body">{kanziSections.howItWorks.description}</p>
            <p className="kanzi-how-intro__note">{kanziSections.howItWorks.agentsNote}</p>
            <p className="kanzi-how-intro__note">{kanziSections.howItWorks.modelNote}</p>
          </div>

          <div className="kanzi-how-viz" aria-hidden="true">
            <svg viewBox="0 0 360 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Input nodes */}
              <circle cx="18" cy="28" r="5" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
              <circle cx="18" cy="50" r="5" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
              <circle cx="18" cy="72" r="5" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.45)" strokeWidth="1" />
              <text x="28" y="32" fill="rgba(255,255,255,0.68)" fontSize="8" fontFamily="Public Sans, sans-serif">Billing</text>
              <text x="28" y="54" fill="rgba(255,255,255,0.68)" fontSize="8" fontFamily="Public Sans, sans-serif">Clinical</text>
              <text x="28" y="76" fill="rgba(255,255,255,0.68)" fontSize="8" fontFamily="Public Sans, sans-serif">Ops</text>

              {/* Inbound flow lines */}
              <line x1="23" y1="28" x2="150" y2="50" stroke="rgba(96,165,250,0.55)" strokeWidth="1.2" strokeDasharray="3 5">
                <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="1.8s" repeatCount="indefinite" />
              </line>
              <line x1="23" y1="50" x2="150" y2="50" stroke="rgba(96,165,250,0.55)" strokeWidth="1.2" strokeDasharray="3 5">
                <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="1.8s" repeatCount="indefinite" begin="0.6s" />
              </line>
              <line x1="23" y1="72" x2="150" y2="50" stroke="rgba(96,165,250,0.55)" strokeWidth="1.2" strokeDasharray="3 5">
                <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="1.8s" repeatCount="indefinite" begin="1.2s" />
              </line>

              {/* Brain pulse ring */}
              <circle cx="180" cy="50" r="30" fill="none" stroke="rgba(59,130,246,0.18)" strokeWidth="1.5">
                <animate attributeName="r" values="30;46;30" dur="4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.6;0;0.6" dur="4s" repeatCount="indefinite" />
              </circle>

              {/* Brain node */}
              <circle cx="180" cy="50" r="30" fill="rgba(59,130,246,0.07)" stroke="rgba(59,130,246,0.42)" strokeWidth="1.2" />
              <text x="180" y="47" textAnchor="middle" fill="rgba(255,255,255,0.88)" fontSize="9" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.1em">BRAIN</text>
              <text x="180" y="59" textAnchor="middle" fill="rgba(96,165,250,0.62)" fontSize="7.5" fontFamily="Public Sans, sans-serif" fontWeight="300" letterSpacing="0.06em">PERSISTENT</text>

              {/* Outbound flow lines */}
              <line x1="210" y1="50" x2="305" y2="28" stroke="rgba(96,165,250,0.55)" strokeWidth="1.2" strokeDasharray="3 5">
                <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="1.8s" repeatCount="indefinite" />
              </line>
              <line x1="210" y1="50" x2="305" y2="50" stroke="rgba(96,165,250,0.55)" strokeWidth="1.2" strokeDasharray="3 5">
                <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="1.8s" repeatCount="indefinite" begin="0.6s" />
              </line>
              <line x1="210" y1="50" x2="305" y2="72" stroke="rgba(96,165,250,0.55)" strokeWidth="1.2" strokeDasharray="3 5">
                <animate attributeName="stroke-dashoffset" from="0" to="-8" dur="1.8s" repeatCount="indefinite" begin="1.2s" />
              </line>

              {/* Agent nodes */}
              <circle cx="310" cy="28" r="5" fill="rgba(59,130,246,0.15)" stroke="rgba(96,165,250,0.7)" strokeWidth="1">
                <animate attributeName="opacity" values="1;0.3;1" dur="2.4s" begin="0s" repeatCount="indefinite" />
              </circle>
              <circle cx="310" cy="50" r="5" fill="rgba(59,130,246,0.15)" stroke="rgba(96,165,250,0.7)" strokeWidth="1">
                <animate attributeName="opacity" values="1;0.3;1" dur="2.4s" begin="0.8s" repeatCount="indefinite" />
              </circle>
              <circle cx="310" cy="72" r="5" fill="rgba(59,130,246,0.15)" stroke="rgba(96,165,250,0.7)" strokeWidth="1">
                <animate attributeName="opacity" values="1;0.3;1" dur="2.4s" begin="1.6s" repeatCount="indefinite" />
              </circle>
              <text x="320" y="32" fill="rgba(96,165,250,0.88)" fontSize="8" fontFamily="Public Sans, sans-serif" fontWeight="300">Agent</text>
              <text x="320" y="54" fill="rgba(96,165,250,0.88)" fontSize="8" fontFamily="Public Sans, sans-serif" fontWeight="300">Agent</text>
              <text x="320" y="76" fill="rgba(96,165,250,0.88)" fontSize="8" fontFamily="Public Sans, sans-serif" fontWeight="300">Agent</text>
            </svg>
          </div>
        </Reveal>

        <Reveal variant="fade-left" className="kanzi-how-split__right">
          <div className="kanzi-how-grid">
            {kanziSections.howItWorks.pillars.map((pillar) => {
              const Icon = icons[pillar.icon];

              return (
                <SpotlightCard key={pillar.title} className="glass-panel kanzi-pillar group">
                  <Icon className="kanzi-pillar__icon" strokeWidth={1.25} aria-hidden="true" />
                  <h3 className="kanzi-pillar__title">{pillar.title}</h3>
                  <p>{pillar.description}</p>
                </SpotlightCard>
              );
            })}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

export default HowItWorks;
